/**
 * Shopify Color Customizer - Main JavaScript Logic
 * Vanilla JavaScript implementation for SVG-based coloring book tool
 */

class ColorCustomizer {
    constructor() {
        this.state = {}; // Tracks overrides per paintable ID
        this.baseColors = {}; // Captures default colors for current template
        this.COLOR_HEX_REGEX = /^#([0-9a-f]{6})$/i;
        // Palette derived from unique fill colors across all Pokeball SVG templates
        this.colorPalette = [
            '#f1f1f1', '#2a2a2a', '#e90d0d', '#790000',
            '#0d68cf', '#00d9ff', '#b03fe4', '#591a8c',
            '#df21af', '#ff7edd', '#ebdf43', '#fa9f1f',
            '#05ae1f', '#c0c0c0', '#e7b931'
        ];
        this.colorNameMap = {
            '#f1f1f1': 'White',
            '#2a2a2a': 'Black',
            '#e90d0d': 'Red',
            '#e70e0e': 'Red',
            '#790000': 'Maroon',
            '#0d68cf': 'Blue',
            '#00d9ff': 'Cyan',
            '#b03fe4': 'Purple',
            '#591a8c': 'Indigo',
            '#df21af': 'Magenta',
            '#ff7edd': 'Pink',
            '#ebdf43': 'Yellow',
            '#e3e322': 'Yellow',
            '#fa9f1f': 'Orange',
            '#05ae1f': 'Green',
            '#c0c0c0': 'Silver',
            '#e7b931': 'Gold'
        };
        this.activeColor = this.colorPalette[0]; // Default active color
        this.currentTemplate = 'base';
        this.isDev = window?.location?.hostname === 'localhost' || window?.location?.hostname === '127.0.0.1';
        this.svgTemplates = {
            base: new URL('./Pokeball_Coloring_Template_Base_1.svg', import.meta.url).href,
            rocket: new URL('./Pokeball_Coloring_Template_Rocket_1.svg', import.meta.url).href,
            great: new URL('./Pokeball_Coloring_Template_Great_1.svg', import.meta.url).href,
            ultra: new URL('./Pokeball_Coloring_Template_Ultra_1.svg', import.meta.url).href,
            master: new URL('./Pokeball_Coloring_Template_Master_1.svg', import.meta.url).href
        };
        
        // Shopify configuration (to be updated with actual values)
        this.customColorVariantId = 44419345285256; // Replace with actual variant ID from Shopify admin
        this.productId = 7991930880136; // Replace with actual product ID
        
        this.init();
    }
    
    init() {
        this.setupEventListeners();
        this.loadTemplate(this.currentTemplate);
        this.renderColorPalette();
    }
    
    setupEventListeners() {
        // Template selection
        const templateSelect = document.getElementById('template-select');
        if (templateSelect) {
            templateSelect.addEventListener('change', (e) => {
                this.currentTemplate = e.target.value;
                this.loadTemplate(this.currentTemplate);
            });
        }
        
        // Order button
        const orderBtn = document.getElementById('order-btn');
        if (orderBtn) {
            orderBtn.addEventListener('click', () => {
                this.handleOrder();
            });
        }
    }
    
    loadTemplate(templateName) {
        const container = document.querySelector('.svg-container');
        if (!container) return;

        // Ensure loader exists for each load cycle
        container.innerHTML = '<div id="svg-loader">Loading template...</div>';
        const loader = container.querySelector('#svg-loader');
        this.state = {};
        this.baseColors = {};

        return fetch(this.svgTemplates[templateName])
            .then(response => {
                if (!response.ok) throw new Error(`Failed to load template: ${response.status}`);
                return response.text();
            })
            .then(svgText => {
                const sanitized = this.sanitizeSvg(svgText);
                container.innerHTML = sanitized;
                this.captureDefaultColors();
                this.setupPaintableListeners();
                this.applyStateToSVG();
                const orderBtn = document.getElementById('order-btn');
                if (orderBtn) {
                    orderBtn.disabled = false; // Allow ordering even with defaults
                }
            })
            .catch(error => {
                if (loader) loader.textContent = `Error loading template: ${error.message}`;
                this.handleError('Template load failed', error);
            });
    }

    setupPaintableListeners() {
        // Remove existing listeners first
        const paintables = document.querySelectorAll('svg [paintable]');
        paintables.forEach(element => {
            element.replaceWith(element.cloneNode(true));
        });

        // Add new listeners
        document.querySelectorAll('svg [paintable]').forEach(element => {
            if (element.tagName.toLowerCase() === 'g') return; // skip group wrappers

            element.addEventListener('click', (e) => {
                this.handlePathClick(e.target);
            });

            // Add visual feedback for paintable elements
            element.style.cursor = 'pointer';
        });
    }
    
    handlePathClick(pathElement) {
        const pathId = pathElement.id;
        if (!pathId) return;
        if (pathElement.tagName.toLowerCase() === 'g') return;
        if (!this.isAllowedId(pathId)) return;
        if (!this.isValidHex(this.activeColor)) return;
        
        // Update state
        this.state[pathId] = this.activeColor;
        
        // Apply color to the path
        pathElement.setAttribute('fill', this.activeColor);
        
    }
    
    applyStateToSVG() {
        Object.entries(this.state).forEach(([pathId, color]) => {
            const element = document.getElementById(pathId);
            if (element) {
                element.setAttribute('fill', color);
            }
        });
    }
    
    renderColorPalette() {
        const paletteContainer = document.getElementById('color-palette');
        paletteContainer.innerHTML = '';

        const standardColors = this.colorPalette.slice(0, -2);
        const premiumColors = this.colorPalette.slice(-2);

        paletteContainer.appendChild(this.buildPaletteGroup('Colors', standardColors));
        paletteContainer.appendChild(this.buildPaletteGroup('Premium', premiumColors, true));
    }

    buildPaletteGroup(label, colors, isPremium = false) {
        const group = document.createElement('div');
        group.className = `palette-group${isPremium ? ' palette-group--premium' : ''}`;

        const labelEl = document.createElement('span');
        labelEl.className = 'palette-label';
        labelEl.textContent = isPremium ? `${label} (Silver & Gold)` : label;
        group.appendChild(labelEl);

        const optionsEl = document.createElement('div');
        optionsEl.className = 'palette-options';

        colors.forEach(color => {
            const colorBtn = document.createElement('button');
            colorBtn.className = 'color-option';
            colorBtn.style.backgroundColor = color;
            colorBtn.dataset.color = color;
            colorBtn.title = `${this.getColorName(color)} (${color})`;

            if (color === this.activeColor) {
                colorBtn.classList.add('active');
            }

            colorBtn.addEventListener('click', () => {
                this.setActiveColor(color);
            });

            optionsEl.appendChild(colorBtn);
        });

        group.appendChild(optionsEl);
        return group;
    }
    
    setActiveColor(color) {
        if (!this.isValidHex(color)) return;
        this.activeColor = color;
        document.querySelectorAll('.color-option').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.color === color);
        });
    }

    handleOrder() {
        const properties = this.buildLineItemProperties();

        const hasProperties = Object.keys(properties).length > 0;
        if (!hasProperties) {
            this.handleError('No paintable data available', new Error('Empty properties'));
            return;
        }

        // Prepare cart payload
        const cartPayload = {
            id: this.customColorVariantId,
            quantity: 1,
            properties: properties
        };

        // Development mode: log and bail to avoid accidental cart writes
        if (this.isDev) {
            console.log('Cart payload (dev):', cartPayload);
            alert('In development mode - Order button is disabled.\n\nWould send to Shopify:\n' + JSON.stringify(cartPayload, null, 2));
            return;
        }

        this.safeAddToCart(cartPayload);
    }

    safeAddToCart(cartPayload, attempt = 1) {
        const MAX_ATTEMPTS = 3;
        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartPayload)
        })
        .then(response => {
            if (response.status === 429 && attempt < MAX_ATTEMPTS) {
                const delay = 300 * attempt; // simple backoff
                return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
                    this.safeAddToCart(cartPayload, attempt + 1)
                );
            }
            if (!response.ok) {
                throw new Error(`Cart add failed with status ${response.status}`);
            }
            return response.json();
        })
        .then(() => {
            window.location.href = '/cart';
        })
        .catch(error => {
            this.handleError('Failed to add item to cart', error);
            alert('Could not add the custom item to cart. Please try again.');
        });
    }

    buildLineItemProperties() {
        const properties = {};
        const paintables = document.querySelectorAll('svg [paintable]');
        const MAX_PROPERTIES = 20;
        const MAX_KEY_LENGTH = 255;
        const MAX_VALUE_LENGTH = 1000;
        let count = 0;

        paintables.forEach(element => {
            if (!element.id || element.tagName.toLowerCase() === 'g') return;
            const pathId = element.id;
            if (!this.isAllowedId(pathId)) return;
            const hex = this.state[pathId] || this.baseColors[pathId] || this.getElementFillHex(element);
            if (!hex) return;
            const label = this.formatLabel(pathId);
            const colorName = this.getColorName(hex);
            const safeLabel = label.slice(0, MAX_KEY_LENGTH);
            const safeValue = `${colorName} (${hex})`.slice(0, MAX_VALUE_LENGTH);
            if (count < MAX_PROPERTIES) {
                properties[safeLabel] = safeValue;
                count += 1;
            }
        });

        return properties;
    }

    formatLabel(pathId) {
        return pathId
            .split('-')
            .map(part => part
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '))
            .join('-');
    }

    captureDefaultColors() {
        this.baseColors = {};
        document.querySelectorAll('svg [paintable]').forEach(element => {
            if (!element.id || element.tagName.toLowerCase() === 'g') return;
            if (!this.isAllowedId(element.id)) return;
            const hex = this.getElementFillHex(element);
            if (hex) {
                this.baseColors[element.id] = hex;
            }
        });
    }

    getElementFillHex(element) {
        const rawFill = element.getAttribute('fill') || window.getComputedStyle(element).getPropertyValue('fill');
        return this.normalizeHex(rawFill);
    }

    normalizeHex(color) {
        if (!color) return null;
        if (color === 'none') return null;

        // Already hex
        if (color.startsWith('#')) {
            const value = color.toLowerCase();
            if (value.length === 4) {
                return '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
            }
            return value;
        }

        // rgb() or rgba()
        const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (rgbMatch) {
            const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, '0');
            const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, '0');
            const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        }

        return null;
    }

    getColorName(hex) {
        if (!hex) return 'Unknown';
        const normalized = this.normalizeHex(hex);
        return this.colorNameMap[normalized] || normalized;
    }

    isValidHex(color) {
        if (!color) return false;
        const normalized = this.normalizeHex(color);
        return !!normalized && this.COLOR_HEX_REGEX.test(normalized);
    }

    isAllowedId(id) {
        return /^[a-zA-Z0-9_-]+$/.test(id);
    }

    sanitizeSvg(svgText) {
        try {
            const Parser = (typeof DOMParser !== 'undefined' ? DOMParser : (typeof window !== 'undefined' ? window.DOMParser : null));
            if (!Parser) {
                throw new Error('DOMParser is not available in this environment');
            }
            const normalizedSvgText = svgText.replace(/paintable(?=(\s|>))/g, 'paintable="true"');
            const parser = new Parser();
            const doc = parser.parseFromString(normalizedSvgText, 'image/svg+xml');

            if (doc.querySelector('parsererror')) {
                throw new Error('Invalid SVG markup');
            }

            const forbiddenTags = ['script', 'foreignObject', 'iframe', 'object', 'embed'];
            forbiddenTags.forEach(tag => {
                doc.querySelectorAll(tag).forEach(node => node.remove());
            });

            // Remove event handlers and style/script URLs
            doc.querySelectorAll('*').forEach(node => {
                [...node.attributes].forEach(attr => {
                    const name = attr.name.toLowerCase();
                    if (name.startsWith('on')) {
                        node.removeAttribute(attr.name);
                    }
                    if (name === 'href' || name === 'xlink:href') {
                        const value = attr.value || '';
                        if (/^\s*javascript:/i.test(value)) {
                            node.removeAttribute(attr.name);
                        }
                    }
                });
            });

            // Strip paintable elements with unexpected IDs
            doc.querySelectorAll('[paintable]').forEach(node => {
                if (!node.id || !this.isAllowedId(node.id)) {
                    node.removeAttribute('paintable');
                }
            });

            return doc.documentElement.outerHTML;
        } catch (error) {
            this.handleError('SVG sanitization failed', error);
            return '<svg></svg>';
        }
    }

    handleError(message, error) {
        console.error(message, error);
        if (this.isDev) {
            alert(`${message}. Check console for details.`);
        }
    }
}

// Initialize the customizer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.customizer = new ColorCustomizer();
});

export default ColorCustomizer;
